import React, { useState } from 'react';
import { postJson } from '../services/http';
import TermsOfUse from './TermsOfUse';

/**
 * Check if the current user has already accepted terms.
 * Checks both the dedicated key and the stored user object.
 */
function hasAcceptedTerms() {
  if (localStorage.getItem('termsAcceptedAt')) return true;
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.termsAcceptedAt) return true;
  } catch {}
  return false;
}

/**
 * Best-effort "is the visitor logged in?" check for the gate. The auth cookie
 * is httpOnly so we can't read it from JS — userId is written to localStorage
 * alongside the cookie at login time and is the next-best signal. Anonymous
 * visitors (no userId stored) must not be shown the terms gate: a number of
 * routes under /:tenant/* are intentionally public — eventAction (raffle /
 * auction entry), publicEventHistory, memberLogin, reset-password — and the
 * gate would otherwise intercept them, POST /api/users/accept-terms with no
 * session, get 401, and bounce the visitor to /login via axiosBoot's global
 * fetch wrapper.
 */
function hasClientSession() {
  return !!(localStorage.getItem('userId') || localStorage.getItem('ownerid'));
}

/**
 * Wraps protected routes — if the user hasn't accepted Terms of Use,
 * shows the terms with an Accept button instead of the app content.
 * Anonymous visitors pass through untouched (see hasClientSession above).
 */
function TermsAcceptanceGate({ children }) {
  const [accepted, setAccepted] = useState(hasAcceptedTerms);
  const [submitting, setSubmitting] = useState(false);

  // Anonymous visitor → no identity to associate terms acceptance with;
  // render the public content directly. Kept AFTER the hooks above so
  // hook order stays stable across renders (Rules of Hooks).
  if (!hasClientSession()) return children;

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      const res = await postJson('/api/users/accept-terms', {});
      if (res.success) {
        // Persist in dedicated key
        localStorage.setItem('termsAcceptedAt', res.termsAcceptedAt);

        // Also update the stored user object so it survives even if the key is lost
        try {
          const user = JSON.parse(localStorage.getItem('user'));
          if (user) {
            user.termsAcceptedAt = res.termsAcceptedAt;
            localStorage.setItem('user', JSON.stringify(user));
          }
        } catch {}

        setAccepted(true);
      }
    } catch (err) {
      console.error('Failed to accept terms:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (accepted) return children;

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', color: '#222' }}>
      <TermsOfUse hideFooter />
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: '#fff',
        borderTop: '1px solid #ddd',
        padding: '12px 20px',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 1000,
      }}>
        <button
          className="btn btn-primary"
          onClick={handleAccept}
          disabled={submitting}
          style={{ minWidth: 220, fontSize: '1rem', padding: '10px 24px' }}
        >
          {submitting ? 'Saving...' : 'I Accept the Terms of Use'}
        </button>
      </div>
    </div>
  );
}

export default TermsAcceptanceGate;
